import {
  clearBusinessTravellerTripRequestRoomAssignments,
  getBusinessTravellerTripRequestRoomAssignments,
} from 'businessTraveller/storage/businessTravellerStorage'

/**
 * Checks if there is an accommodation trip request to restore.
 * This is used to determine if we should apply trip request details to traveller forms.
 */
export function hasAccommodationTripRequestToRestore(): boolean {
  const tripRequestState = getBusinessTravellerTripRequestRoomAssignments()
  return !!(tripRequestState?.assignments && tripRequestState.assignments.length > 0)
}

/**
 * Applies accommodation trip request traveller details to existing traveller forms.
 * This is called after restoring a cart from an approved trip request to:
 * 1. Restore room assignments (roomIndex)
 * 2. Pre-fill traveller details (firstName, lastName, email, phonePrefix, phone)
 *
 * The guestTravellerDetails contains the actual booking details entered at trip request time,
 * which may differ from employee profiles (e.g., legal name vs display name).
 *
 * @param travellerForms - The traveller forms built by getLEBTTravellerForms
 * @returns Updated traveller forms with trip request details applied
 */
export function applyAccommodationTripRequestDetailsToForms(
  travellerForms: Array<App.Checkout.TravellerForm>,
): Array<App.Checkout.TravellerForm> {
  const tripRequestState = getBusinessTravellerTripRequestRoomAssignments()

  if (!tripRequestState?.assignments || tripRequestState.assignments.length === 0) {
    return travellerForms
  }

  const assignments = tripRequestState.assignments

  // Clear after reading - one-time use
  clearBusinessTravellerTripRequestRoomAssignments()

  // Build assignment lookup by employeeId or guestId
  const assignmentMap = new Map(
    assignments.map(a => [a.employeeId ?? a.guestId!, a]),
  )

  return travellerForms.map((form) => {
    const key = form.employeeId ?? form.guestId
    const assignment = key ? assignmentMap.get(key) : undefined
    return assignment ? applyAssignmentToForm(form, assignment) : form
  })
}

/**
 * Applies a single trip request assignment to a traveller form.
 */
function applyAssignmentToForm(
  form: App.Checkout.TravellerForm,
  assignment: App.BusinessTraveller.TripRequestRoomAssignment,
): App.Checkout.TravellerForm {
  const details = assignment.guestTravellerDetails

  return {
    ...form,
    roomIndex: assignment.roomIndex,
    data: {
      ...form.data,
      // Only override if trip request has the value
      ...(details?.firstName && { firstName: details.firstName }),
      ...(details?.lastName && { lastName: details.lastName }),
      ...(details?.email && { email: details.email }),
      ...(details?.phonePrefix && { phonePrefix: details.phonePrefix }),
      ...(details?.phone && { phone: details.phone }),
    },
  }
}
